import type { InputFrame } from "../input";
import { railHit, surfaceAt, wallNormal } from "../level";
import type { Skater } from "../skater";
import { noteAirTrick } from "./air";
import { bail } from "./bail";
import { hillWall } from "./hill";
import { creditSpin, land } from "./landing";
import { wrapAngle } from "./math";
import type { SkaterEvent } from "./state";
import {
  G,
  PLANT_OUT_BASE,
  PLANT_OUT_KEEP,
  PLANT_POINTS,
  PLANT_POP,
  PLANT_STICK,
  STEP_UP,
  WALL_BAIL_SPEED,
  WALL_GRACE,
  WALLIE_KEEP,
  WALLIE_OUT,
  WALLIE_POINTS,
  WALLIE_POP,
  WALLRIDE_G,
  WALLRIDE_MIN_SPEED,
  WALLRIDE_PER_SECOND,
  WALLRIDE_POINTS,
  WALLRIDE_PROBE,
  WALLRIDE_TILT_UP,
  WALLRIDE_TIME,
  WALLRIDE_VY_MAX,
  WALLRIDE_VY_MIN,
} from "./tuning";

/**
 * Flew into a wall. You hang on it for a beat (WALL_GRACE): ollie just
 * before contact or during that beat plants off it. No plant and a hard hit
 * bails once the beat is up; a soft one lets you drop down the wall.
 */
export function hitWallInAir(sk: Skater, nx: number, nz: number): void {
  const air = sk.air!;
  const n = wallNormal(sk.level, nx, nz, sk.x, sk.z);
  const into = -(sk.vx * n.x + sk.vz * n.z);
  if (into <= 0.5) return;
  // Keep only the along-the-wall part of the motion while against it.
  const alongX = sk.vx + into * n.x;
  const alongZ = sk.vz + into * n.z;
  air.wall = { n, t: 0, impact: into, planted: false, alongX, alongZ };
  sk.vx = alongX;
  sk.vz = alongZ;
  if (sk.plantBuffer > 0 || (sk.plantBufferGrind > 0 && headOnWall(sk))) startPlant(sk);
  else if (sk.plantBufferGrind > 0 || sk.grindHeld) tryWallride(sk);
}

/**
 * Flew into a rail below the bar: no hanging on it or planting off it like
 * a wall. A hard hit bails; a soft one loses the push into the rail and
 * carries on along it. Returns true on a bail.
 */
export function hitRailInAir(sk: Skater, n: { x: number; z: number }, events: SkaterEvent[]): boolean {
  const into = -(sk.vx * n.x + sk.vz * n.z);
  if (into > WALL_BAIL_SPEED) {
    sk.vx = sk.vz = 0;
    bail(sk, events);
    return true;
  }
  if (into > 0) {
    sk.vx += into * n.x;
    sk.vz += into * n.z;
  }
  return false;
}

/**
 * The grind button (Y / Triangle) plants only when you hit the wall fairly
 * head-on (within 45 degrees); shallow-angle hits are saved for wallrides.
 */
export function headOnWall(sk: Skater): boolean {
  const air = sk.air!;
  const w = air.wall!;
  return w.impact >= Math.hypot(w.alongX, w.alongZ);
}

export function startPlant(sk: Skater): void {
  const air = sk.air!;
  const w = air.wall!;
  w.planted = true;
  w.t = 0;
  sk.plantBuffer = 0;
  sk.plantBufferGrind = 0;
  sk.vx = sk.vy = sk.vz = 0;
  air.vertAxis = null;
  noteAirTrick(sk, sk.addTrick("Wallplant", PLANT_POINTS));
}

/**
 * Up + grind into a wall at a shallow angle (THPS 3+4): stick to it and
 * ride along it. Whatever spin and tricks came before score as their own
 * air; the ride is a new entry in the same combo. False (and nothing
 * changes) when the hit is head-on or too slow along the wall.
 */
export function tryWallride(sk: Skater): boolean {
  const air = sk.air!;
  const w = air.wall!;
  const along = Math.hypot(w.alongX, w.alongZ);
  if (headOnWall(sk) || along < WALLRIDE_MIN_SPEED) return false;
  creditSpin(sk);
  air.wall = null;
  air.flip = null;
  air.grab = null;
  air.vertAxis = null;
  sk.plantBufferGrind = 0;
  sk.grindBuffer = 0;
  sk.vx = w.alongX;
  sk.vz = w.alongZ;
  sk.vy = Math.max(WALLRIDE_VY_MIN, Math.min(WALLRIDE_VY_MAX, sk.vy));
  // Keep whichever end of the board was already leading, like a grind.
  const travelYaw = Math.atan2(w.alongX, w.alongZ);
  sk.yaw = Math.abs(wrapAngle(travelYaw - sk.yaw)) <= Math.PI / 2 ? travelYaw : travelYaw + Math.PI;
  const l = Math.hypot(w.n.x, WALLRIDE_TILT_UP, w.n.z);
  sk.normal = { x: w.n.x / l, y: WALLRIDE_TILT_UP / l, z: w.n.z / l };
  air.wallride = {
    n: w.n,
    index: sk.addTrick("Wallride", WALLRIDE_POINTS),
    t: 0,
  };
  return true;
}

/** The wall beside a wallride is still there at (x, z), up to height y. */
export function wallBeside(sk: Skater, n: { x: number; z: number }, x: number, z: number, y: number): boolean {
  const px = x - n.x * WALLRIDE_PROBE;
  const pz = z - n.z * WALLRIDE_PROBE;
  return sk.outOfBounds(px, pz) || surfaceAt(sk.level, px, pz).h > y + STEP_UP;
}

/**
 * Along the wall, gravity easing you down, scoring as you go. Ollie pops a
 * Wallie up and away from it; running out of wall or time drops you off
 * into a normal air; the ground coming up lands you.
 */
export function stepWallride(sk: Skater, input: InputFrame, dt: number, events: SkaterEvent[]): void {
  const air = sk.air!;
  const w = air.wallride!;
  w.t += dt;
  sk.combo.addPoints(w.index, WALLRIDE_PER_SECOND * dt);
  sk.crouch += (0.5 - sk.crouch) * Math.min(1, dt * 10);
  if (input.pressed.ollie) {
    air.wallride = null;
    sk.plantBuffer = 0;
    sk.vx = sk.vx * WALLIE_KEEP + w.n.x * WALLIE_OUT;
    sk.vz = sk.vz * WALLIE_KEEP + w.n.z * WALLIE_OUT;
    sk.vy = WALLIE_POP * sk.special.boost;
    sk.normal = { x: 0, y: 1, z: 0 };
    noteAirTrick(sk, sk.addTrick("Wallie", WALLIE_POINTS));
    return;
  }
  sk.vy -= WALLRIDE_G * dt;
  const nx = sk.x + sk.vx * dt;
  const nz = sk.z + sk.vz * dt;
  const ny = sk.y + sk.vy * dt;
  const blocked =
    sk.outOfBounds(nx, nz) ||
    surfaceAt(sk.level, nx, nz).h > ny + STEP_UP ||
    railHit(sk.level, sk.x, sk.z, nx, nz, ny) !== null;
  if (w.t >= WALLRIDE_TIME || blocked || !wallBeside(sk, w.n, nx, nz, ny)) {
    // Off the wall: carry on as a normal air (a wall ahead is the air's to handle).
    air.wallride = null;
    sk.normal = { x: 0, y: 1, z: 0 };
    return;
  }
  sk.x = nx;
  sk.z = nz;
  const floor = surfaceAt(sk.level, nx, nz);
  if (ny <= floor.h) {
    air.wallride = null;
    land(sk, floor, events);
    return;
  }
  sk.y = ny;
}

export function stepWall(sk: Skater, input: InputFrame, dt: number, events: SkaterEvent[]): void {
  const air = sk.air!;
  const w = air.wall!;
  w.t += dt;
  if (w.planted) {
    // Stuck to the wall with a foot on it, then kick straight back out.
    sk.crouch += (0.95 - sk.crouch) * Math.min(1, dt * 20);
    if (w.t < PLANT_STICK) return;
    const out = PLANT_OUT_BASE + PLANT_OUT_KEEP * w.impact;
    sk.vx = w.n.x * out + w.alongX * 0.4;
    sk.vz = w.n.z * out + w.alongZ * 0.4;
    sk.vy = PLANT_POP;
    sk.yaw = Math.atan2(sk.vx, sk.vz);
    air.wall = null;
    return;
  }
  if (input.pressed.ollie || (input.pressed.grind && headOnWall(sk))) {
    startPlant(sk);
    return;
  }
  if (input.held.grind && tryWallride(sk)) return;
  if (w.t >= WALL_GRACE) {
    air.wall = null;
    // On the hill (#195) hanging on a wall without riding or planting off it ends the run.
    if (hillWall(sk, events)) return;
    if (w.impact > WALL_BAIL_SPEED) {
      sk.vx = sk.vz = 0;
      bail(sk, events);
    }
    return;
  }
  // Hanging on the wall: sink slowly, drift along it, land if the ground comes up.
  sk.vy -= G * 0.5 * dt;
  const nx = sk.x + sk.vx * dt;
  const nz = sk.z + sk.vz * dt;
  const ny = sk.y + sk.vy * dt;
  const s = surfaceAt(sk.level, nx, nz);
  if (s.h - sk.y > STEP_UP || sk.outOfBounds(nx, nz) || railHit(sk.level, sk.x, sk.z, nx, nz, sk.y)) {
    sk.vx = sk.vz = 0;
  } else {
    sk.x = nx;
    sk.z = nz;
  }
  const floor = surfaceAt(sk.level, sk.x, sk.z);
  if (ny <= floor.h) {
    air.wall = null;
    land(sk, floor, events);
    return;
  }
  sk.y = ny;
}
